import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, ImageIcon, Loader2, CheckCircle2, AlertCircle, Lock, Film } from "lucide-react";
import MiniHeader from "@/components/site/MiniHeader";
import PaymentCard from "@/components/site/PaymentCard";
import MusicPlayer from "@/components/site/MusicPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = "image/*,.heic,.heif";
const MAX_MB = 30;
const CONCURRENCY = 3;

function Thumb({ token, photo, onRemove }) {
  return (
    <div data-testid={`uploaded-photo-${photo.id}`} className="group relative aspect-square rounded-xl overflow-hidden border border-[rgba(197,165,90,0.2)] bg-[#141311]">
      {photo.has_thumb ? (
        <img src={`${API}/upload/${token}/photos/${photo.id}/thumb`} alt={photo.original_filename} loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-2 text-center">
          <ImageIcon size={22} className="text-[#C5A55A]" />
          <span className="text-[10px] text-[#A0988E] break-all line-clamp-2">{photo.original_filename}</span>
        </div>
      )}
      <button
        data-testid={`remove-photo-${photo.id}`}
        onClick={() => onRemove(photo)}
        aria-label="Remove photo"
        className="absolute top-1.5 right-1.5 h-7 w-7 inline-flex items-center justify-center rounded-full bg-[rgba(11,10,9,0.75)] border border-[rgba(212,175,55,0.3)] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
      >
        <X size={13} className="text-[#F5F0E8]" />
      </button>
    </div>
  );
}

function QueueItem({ item }) {
  return (
    <div className="flex items-center gap-3 text-sm py-2">
      {item.status === "error" ? <AlertCircle size={15} className="text-red-400 shrink-0" /> :
        item.status === "done" ? <CheckCircle2 size={15} className="text-[#D4AF37] shrink-0" /> :
        <Loader2 size={15} className="text-[#C5A55A] animate-spin shrink-0" />}
      <span className="truncate flex-1 text-[#F5F0E8]/85">{item.file.name}</span>
      {item.status === "error" ? <span className="text-xs text-red-300 text-right">{item.error}</span> :
        <span className="text-xs text-[#A0988E] w-10 text-right">{item.progress}%</span>}
    </div>
  );
}

export default function UploadPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const busy = queue.some((q) => q.status === "uploading" || q.status === "waiting");

  useEffect(() => {
    axios.get(`${API}/upload/${token}`).then((r) => setSession(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const patch = (id, data) => setQueue((q) => q.map((x) => (x.id === id ? { ...x, ...data } : x)));

  const uploadOne = async (item) => {
    patch(item.id, { status: "uploading" });
    const fd = new FormData();
    fd.append("file", item.file);
    try {
      const { data } = await axios.post(`${API}/upload/${token}/photos`, fd, {
        onUploadProgress: (e) => e.total && patch(item.id, { progress: Math.round((e.loaded / e.total) * 100) }),
      });
      patch(item.id, { status: "done", progress: 100 });
      setSession((s) => ({ ...s, photos: [...s.photos, data] }));
      return true;
    } catch (err) {
      patch(item.id, { status: "error", error: err?.response?.data?.detail || "Upload failed" });
      return false;
    }
  };

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !session) return;
    const room = session.max_photos - session.photos.length;
    const items = files.slice(0, Math.max(room, 0)).map((file) => ({
      id: `${file.name}-${file.size}-${Math.random()}`, file, progress: 0,
      status: file.size > MAX_MB * 1024 * 1024 ? "error" : "waiting",
      error: file.size > MAX_MB * 1024 * 1024 ? `Over ${MAX_MB} MB` : null,
    }));
    if (files.length > room) toast.error(`Only ${Math.max(room, 0)} more photos can be added.`);
    setQueue((q) => [...items, ...q.filter((x) => x.status !== "done")]);

    const todo = items.filter((i) => i.status === "waiting");
    let ok = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, todo.length) }, async () => {
      while (todo.length) if (await uploadOne(todo.shift())) ok += 1;
    });
    await Promise.all(workers);
    if (ok) {
      axios.post(`${API}/upload/${token}/done`).catch(() => {});
      toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded safely.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, token]);

  const removePhoto = async (photo) => {
    try {
      await axios.delete(`${API}/upload/${token}/photos/${photo.id}`);
      setSession((s) => ({ ...s, photos: s.photos.filter((p) => p.id !== photo.id) }));
    } catch {
      toast.error("Could not remove that photo. Please try again.");
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen"><MiniHeader />
        <div data-testid="upload-invalid-link" className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="serif text-4xl font-light text-[#F5F0E8]">This link isn&apos;t valid</h1>
          <p className="mt-4 text-[#A0988E] spectral">Please use the private link from your email, or write to us at eternallegacytributes@gmail.com and we&apos;ll send it again.</p>
        </div>
      </div>
    );
  }
  if (!session) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A55A]" /></div>;
  }

  const count = session.photos.length;
  return (
    <div data-testid="upload-page" className="min-h-screen">
      <MiniHeader right={<span className="hidden sm:inline-flex items-center gap-2 text-xs text-[#A0988E]"><Lock size={13} className="text-[#D4AF37]" /> Private upload link</span>} />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 pb-32">
        {session.film_ready && (
          <a data-testid="upload-film-ready-banner" href={`/film/${token}`}
             className="mb-10 flex items-center gap-4 card-surface p-5 border-[rgba(212,175,55,0.5)] hover:bg-[rgba(212,175,55,0.06)] transition-colors">
            <Film size={22} className="text-[#D4AF37] shrink-0" strokeWidth={1.4} />
            <span className="flex-1">
              <span className="block serif text-xl text-[#F5F0E8]">{session.loved_one_name}&apos;s tribute film is ready</span>
              <span className="text-sm text-[#A0988E]">Tap to watch and download it</span>
            </span>
          </a>
        )}
        <div className="overline mb-4">Share their photos</div>
        <h1 className="serif text-4xl sm:text-5xl font-light leading-[1.08] text-[#F5F0E8]">
          Photos of <span className="italic text-[#E6B89C]" data-testid="upload-loved-one-name">{session.loved_one_name}</span>
        </h1>
        <p className="mt-5 max-w-2xl text-[#A0988E] spectral leading-relaxed">
          Add photos from any era of their life — phone pictures, scans, even faded prints. You can come back to this
          page any time and add more a few at a time. Up to 60 photos are included; send your favourites first.
        </p>

        <div
          data-testid="upload-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          className={`mt-10 cursor-pointer rounded-[22px] border-2 border-dashed px-6 py-14 text-center transition-colors ${
            dragging ? "border-[#D4AF37] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(197,165,90,0.3)] bg-[rgba(20,19,17,0.5)] hover:border-[rgba(212,175,55,0.6)]"
          }`}
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.06)]">
            <UploadCloud size={24} className="text-[#D4AF37]" strokeWidth={1.4} />
          </span>
          <div className="serif text-2xl text-[#F5F0E8] mt-5">Tap to choose photos</div>
          <div className="text-sm text-[#A0988E] mt-2">or drag them here · JPG, PNG, HEIC · up to {MAX_MB} MB each</div>
          <input
            ref={inputRef}
            data-testid="upload-file-input"
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
        </div>

        {queue.length > 0 && (
          <div data-testid="upload-queue" className="mt-6 card-surface px-5 py-3 divide-y divide-[rgba(197,165,90,0.1)]">
            {queue.map((q) => <QueueItem key={q.id} item={q} />)}
          </div>
        )}

        <div className="mt-12 flex items-end justify-between gap-4 flex-wrap">
          <h2 className="serif text-2xl text-[#F5F0E8]">Your photos</h2>
          <span data-testid="upload-photo-count" className="text-sm text-[#A0988E]">
            {count} uploaded {count > 60 && <span className="text-[#E6B89C]">· over 60, we&apos;ll be in touch</span>}
          </span>
        </div>
        {count === 0 ? (
          <p className="mt-4 text-[#A0988E] spectral italic">No photos yet. They&apos;ll appear here as they upload.</p>
        ) : (
          <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {session.photos.map((p) => <Thumb key={p.id} token={token} photo={p} onRemove={removePhoto} />)}
          </div>
        )}

        {count > 0 && !busy && (
          <div data-testid="upload-complete-note" className="mt-10 card-surface p-6 flex items-start gap-4">
            <CheckCircle2 size={22} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.4} />
            <p className="text-[#F5F0E8]/90 spectral leading-relaxed">
              Thank you. Your photos are safely with us and our team has been notified.
              {session.payment.status === "unpaid" ? " One last step — payment, just below." : " You can close this page."}
              {" "}Feel free to add more photos any time.
            </p>
          </div>
        )}
        {count > 0 && !busy && (
          <PaymentCard token={token} payment={session.payment} lovedOne={session.loved_one_name}
                       onChange={(payment) => setSession((s) => ({ ...s, payment }))} />
        )}
      </main>
      <MusicPlayer volume={0.22} ownerControls={false} />
    </div>
  );
}
